import React, { useState } from "react";
import axios from "axios";
import { Send, X, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

// crypto.randomUUID() only exists in secure contexts (HTTPS/localhost); it is
// undefined over plain http:// and would throw, white-screening the dialog.
// This helper falls back to a good-enough unique id for React keys.
const uid = () =>
  (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    ? crypto.randomUUID()
    : `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function BatchProofApprovalDialog({ eventId, eventName, open, onOpenChange, onSuccess }) {
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [artworks, setArtworks] = useState([
    { _key: uid(), artwork_name: "", google_drive_link: "" }
  ]);
  const [sending, setSending] = useState(false);

  // 6 fixed artwork types
  const ARTWORK_TYPES = [
    "Name Badge",
    "Registration List",
    "Pocket Folder",
    "Feedback Form",
    "Agenda",
    "Roller Banner"
  ];

  const addArtwork = () => {
    setArtworks([...artworks, { _key: uid(), artwork_name: "", google_drive_link: "" }]);
  };

  const removeArtwork = (index) => {
    if (artworks.length === 1) {
      toast.error("At least one artwork is required");
      return;
    }
    setArtworks(artworks.filter((_, i) => i !== index));
  };

  const updateArtwork = (index, field, value) => {
    const updated = [...artworks];
    updated[index][field] = value;
    setArtworks(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!customerName || !customerEmail) {
      toast.error("Please fill in customer details");
      return;
    }

    for (let i = 0; i < artworks.length; i++) {
      if (!artworks[i].artwork_name || !artworks[i].google_drive_link) {
        toast.error(`Please fill in all details for artwork ${i + 1}`);
        return;
      }
    }

    setSending(true);
    try {
      await axios.post(`${API}/api/proof/proof-approvals/batch`, {
        event_id: eventId,
        event_code: eventName,
        customer_name: customerName,
        customer_email: customerEmail,
        notes: notes,
        artworks: artworks.map(({ _key, ...rest }) => rest)
      });
      
      toast.success("Proof approval request sent to customer!");
      
      // Reset form
      setCustomerName("");
      setCustomerEmail("");
      setNotes("");
      setArtworks([{ _key: uid(), artwork_name: "", google_drive_link: "" }]);
      
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send approval request");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Send Multiple Proofs for Approval</DialogTitle>
          <DialogDescription>
            Send multiple artworks in one email to the customer
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Customer Details */}
          <div className="bg-gray-50 p-4 rounded border-2">
            <h3 className="font-semibold mb-3">Customer Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Customer Name *</label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="John Doe"
                  className="rounded-none border-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Customer Email *</label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="rounded-none border-2"
                  required
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium mb-1">Event Code</label>
              <Input
                value={eventName}
                disabled
                className="rounded-none border-2 bg-gray-100"
              />
            </div>
          </div>

          {/* Artworks */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Artworks ({artworks.length})</h3>
              <Button
                type="button"
                onClick={addArtwork}
                size="sm"
                variant="outline"
                className="rounded-none"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Artwork
              </Button>
            </div>

            <div className="space-y-4">
              {artworks.map((artwork, index) => (
                <div key={artwork._key} className="border-2 p-4 rounded bg-white relative">
                  <div className="absolute top-2 right-2">
                    <Button
                      type="button"
                      onClick={() => removeArtwork(index)}
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="pr-10">
                    <p className="text-sm font-semibold text-gray-600 mb-3">Artwork #{index + 1}</p>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Artwork Name *</label>
                        <Select
                          value={artwork.artwork_name}
                          onValueChange={(value) => updateArtwork(index, 'artwork_name', value)}
                        >
                          <SelectTrigger className="rounded-none border-2">
                            <SelectValue placeholder="Select artwork type" />
                          </SelectTrigger>
                          <SelectContent>
                            {ARTWORK_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Google Drive Proof Link *</label>
                        <Input
                          type="url"
                          value={artwork.google_drive_link}
                          onChange={(e) => updateArtwork(index, 'google_drive_link', e.target.value)}
                          placeholder="https://drive.google.com/..."
                          className="rounded-none border-2"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes for the customer..."
              className="rounded-none border-2 min-h-[80px]"
            />
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="submit"
              disabled={sending}
              className="flex-1 bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? "Sending..." : `Send ${artworks.length} Proof${artworks.length > 1 ? 's' : ''} to Customer`}
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="rounded-none"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>

          <p className="text-sm text-gray-600">
            📧 Customer will receive ONE email with all {artworks.length} proof{artworks.length > 1 ? 's' : ''} listed in a table
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
